<template>
  <v-container class="print-page">
    <div v-for="(page, pageIndex) in paginateQRCodeProducts()" :key="pageIndex" class="paper">
      <div class="page-content">
        <div class="default-price-note">Default Price will be used</div>
        <v-col
          v-for="section in page.sections"
          :key="section.productClassId"
          class="product-class-section"
        >
          <div
            v-if="section.productClassId !== 'continued' && section.productClassId !== 'null'"
            class="section-title"
          >
            {{ getProductClassName(section.productClassId) }}
          </div>
          <div class="qr-code-grid">
            <div v-for="product in section.products" :key="product.id" class="qr-code-item">
              <qrcode-vue :value="product.id" :size="150" level="H" />
              <div class="product-info">
                <div class="product-name">{{ getProductName(product.id) }}</div>
                <div class="product-sku">{{ getProductSKU(product.id) }}</div>
                <div class="product-price">
                  {{ getProductDefaultPrice(product.id) }} {{ getProductPriceCurrency(product.id) }}
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </div>
    </div>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import QrcodeVue from "qrcode.vue";
import type { QRCodeProduct, QRCodeProductClass } from "@/catalog/types/qrCodeExport";

interface QRCodePageSection {
  productClassId: string;
  products: QRCodeProduct[];
}

interface QRCodePage {
  sections: QRCodePageSection[];
  count: number;
}

const QRCodeProducts = ref<Record<string, QRCodeProduct>>({});
const productClasses = ref<Record<string, QRCodeProductClass | null | undefined>>({});

const props = defineProps<{
  products: Record<string, QRCodeProduct>;
}>();

// Setup product classes so we have a map of product classes to refer to incase we need it's info
const setupProductClasses = () => {
  productClasses.value = Object.values(QRCodeProducts.value).reduce<
    Record<string, QRCodeProductClass | null | undefined>
  >((obj, product) => {
    obj[getProductClassId(product)] = product.productClass;
    return obj;
  }, {});
};

const getProductClassId = (product: QRCodeProduct): string => {
  if (typeof product.product_class === "string") return product.product_class;
  return product.product_class?.id ?? "null";
};

/*
    This function cleans the QRCodeProducts array and returns an array of product
    classes and their product ids.

    Mainly a helper function for the paginateQRCodeProducts function
*/
const groupProductsByProductClass = (): [string, QRCodeProduct[]][] => {
  // Group products by product class
  const groupedProducts = Object.values(QRCodeProducts.value).reduce<
    Record<string, QRCodeProduct[]>
  >((obj, product) => {
    const key = getProductClassId(product) || "null";
    if (!obj[key]) {
      obj[key] = [];
    }
    obj[key].push(product);
    return obj;
  }, {});

  // This ensures that products with no product class are first
  return [
    ...(groupedProducts.null
      ? ([["null", groupedProducts.null]] as [string, QRCodeProduct[]][])
      : []),
    ...Object.entries(groupedProducts).filter(([key]) => key !== "null"),
  ];
};

const paginateQRCodeProducts = (): QRCodePage[] => {
  // Group products by productClassId
  const groupedProducts = groupProductsByProductClass();

  // Split the products into pages (want each page to have at most 12 products)
  const ITEMS_PER_PAGE = 12;
  const COLUMNS_PER_PAGE = 4;

  const pages: QRCodePage[] = [];

  const productClassIdsVisited = new Set<string>();

  // Keep track of the current page, count will represent the current number of products on the page
  let currentPage: QRCodePage = { sections: [], count: 0 };

  // Iterate over each product class and it's products
  for (const [productClassId, classProducts] of groupedProducts) {
    let remaining = [...classProducts];

    // Keep adding products to the current page until there are no more products to add
    while (remaining.length > 0) {
      const spaceLeft = ITEMS_PER_PAGE - currentPage.count;

      if (remaining.length <= spaceLeft) {
        if (!productClassIdsVisited.has(productClassId)) {
          currentPage.sections.push({
            productClassId,
            products: remaining,
          });
          productClassIdsVisited.add(productClassId);
        } else {
          currentPage.sections.push({
            // Prevent front end from showing same product class header multiple tines
            productClassId: "continued",
            products: remaining,
          });
        }

        // Calculate this product class's total space needed
        const rowsNeeded = Math.ceil(remaining.length / COLUMNS_PER_PAGE);
        const totalSpaceNeeded = rowsNeeded * COLUMNS_PER_PAGE;
        currentPage.count += totalSpaceNeeded;

        if (currentPage.count >= ITEMS_PER_PAGE) {
          pages.push(currentPage);
          currentPage = { sections: [], count: 0 };
        }

        remaining = [];
      } else {
        const chunk = remaining.slice(0, spaceLeft);
        if (!productClassIdsVisited.has(productClassId)) {
          currentPage.sections.push({
            productClassId,
            products: chunk,
          });
          productClassIdsVisited.add(productClassId);
        } else {
          currentPage.sections.push({
            // Prevent front end from showing same product class header multiple tines
            productClassId: "continued",
            products: chunk,
          });
        }
        pages.push(currentPage);
        currentPage = { sections: [], count: 0 };
        remaining = remaining.slice(spaceLeft);
      }
    }
  }

  // If there are any products left, add the current page to the pages array
  if (currentPage.count > 0) {
    pages.push(currentPage);
  }

  return pages;
};

const getProductName = (productId: string) => {
  return QRCodeProducts.value[productId]?.name || "Unknown Product";
};

const getProductSKU = (productId: string) => {
  return QRCodeProducts.value[productId]?.sku || "Unknown SKU";
};

const getProductDefaultPrice = (productId: string) => {
  if (!QRCodeProducts.value[productId]?.defaultPrice) {
    return "";
  }
  const amount = QRCodeProducts.value[productId].defaultPrice?.unit_amount ?? 0;
  return String((amount / 100).toFixed(2));
};

const getProductPriceCurrency = (productId: string) => {
  return QRCodeProducts.value[productId]?.defaultPrice?.currency?.toUpperCase() || "";
};

const getProductClassName = (productClassId: string) => {
  return productClasses.value[productClassId]?.name || "Unknown Product Class";
};

watch(
  () => props.products,
  (newVal) => {
    if (newVal) {
      QRCodeProducts.value = newVal;
      setupProductClasses();
    }
  },
  { immediate: true },
);
</script>
